import { __assign, __read } from "tslib";
import { Button, mergeClasses, tokens, Tooltip, useModalAttributes, } from '@fluentui/react-components';
import { useNovaEventing } from '@nova/react';
import { AppBarEvents } from '@office-start/appbar-events';
import React, { useMemo, useState } from 'react';
import { AppBarErrorBoundary } from './AppBarErrorBoundary';
import { ViewMode } from './Constants';
import { useFlyoutButtonStyles } from './FlyoutButton.styles';
import { EventType } from './FlyoutButton.types';
import useAppBarContext from './hooks/useAppBarContext';
import { AppsIcon, AppsIconSelected } from './Icons';
import { TileIcon } from './TileIcon';
/**
 * Button that launches the More Apps flyout when clicked
 */
export var FlyoutButtonRender = function (_a, forwardRef) {
    var icon = _a.icon, selectedIcon = _a.selectedIcon, selected = _a.selected, text = _a.text, title = _a.title, emptyStateMessage = _a.emptyStateMessage, suggestedAppsAddButtonText = _a.suggestedAppsAddButtonText, suggestedAppsPrivacyLinkText = _a.suggestedAppsPrivacyLinkText, suggestedAppsPermissionsLinkText = _a.suggestedAppsPermissionsLinkText, suggestedAppsTermsOfUseLinkText = _a.suggestedAppsTermsOfUseLinkText, suggestedAppsSectionTitle = _a.suggestedAppsSectionTitle, wide = _a.wide, launchTileBehavior = _a.launchTileBehavior, enabledFlyoutButtonBadgeType = _a.enabledFlyoutButtonBadgeType, flyoutButtonBadgeDescription = _a.flyoutButtonBadgeDescription, flyoutSize = _a.flyoutSize, footerLinks = _a.footerLinks, footerLinkFormat = _a.footerLinkFormat, searchEnabled = _a.searchEnabled, suggestionsEnabled = _a.suggestionsEnabled, searchBoxPlaceholderText = _a.searchBoxPlaceholderText, searchDebounceRate = _a.searchDebounceRate, searchNoResultsMessage = _a.searchNoResultsMessage, searchNoResultsTitle = _a.searchNoResultsTitle, searchResultsSectionTitle = _a.searchResultsSectionTitle, installedAppsSectionAriaLabel = _a.installedAppsSectionAriaLabel, suggestedAppsSectionAriaLabel = _a.suggestedAppsSectionAriaLabel, installedAppsAnnouncementMessage = _a.installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage = _a.suggestedAppsAnnouncementMessage, installedAndSuggestedAppsAnnouncementMessage = _a.installedAndSuggestedAppsAnnouncementMessage, onPinStateChange = _a.onPinStateChange, _b = _a.customImportHandler, customImportHandler = _b === void 0 ? function (p) { return p; } : _b;
    var styles = useFlyoutButtonStyles();
    var _c = __read(useState(null), 2), flyoutOpen = _c[0], setFlyoutOpen = _c[1];
    var _d = __read(useState(false), 2), isHovered = _d[0], setIsHovered = _d[1];
    var targetRef = React.useRef(null);
    /**
     * Sets the selected state of the flyout icon
     * Note: Selected state is only activated when a host app overrides the flyout icon
     */
    var iconSelectedState = React.useMemo(function () {
        return icon && (selected || isHovered || flyoutOpen);
    }, [flyoutOpen, icon, isHovered, selected]);
    var novaEventing = useNovaEventing();
    var appBarContext = useAppBarContext();
    var changeFlyoutState = React.useCallback(function (state) {
        setFlyoutOpen(state);
        if (state) {
            var flyoutOpenedEvent = AppBarEvents.flyoutOpened(function () {
                var _a, _b;
                return {
                    overflowCount: (_b = (_a = appBarContext === null || appBarContext === void 0 ? void 0 : appBarContext.overflowItems) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : -1,
                };
            });
            novaEventing.generateEvent({
                event: flyoutOpenedEvent,
            });
        }
    }, [novaEventing, appBarContext === null || appBarContext === void 0 ? void 0 : appBarContext.overflowItems]);
    var closeFlyout = React.useCallback(function () {
        changeFlyoutState(false);
    }, [changeFlyoutState]);
    React.useEffect(function () {
        window.addEventListener(EventType.RESIZE, closeFlyout);
        window.addEventListener(EventType.SCROLL, closeFlyout);
        return function () {
            window.removeEventListener(EventType.RESIZE, closeFlyout);
            window.removeEventListener(EventType.SCROLL, closeFlyout);
        };
    });
    var onMouseEnter = function () {
        var flyoutHoverEvent = AppBarEvents.flyoutButtonHover(function () {
            return {
                isHovered: true,
            };
        });
        novaEventing.generateEvent({
            event: flyoutHoverEvent,
        });
        setIsHovered(true);
    };
    var onMouseLeave = function () {
        var flyoutHoverEvent = AppBarEvents.flyoutButtonHover(function () {
            return {
                isHovered: false,
            };
        });
        novaEventing.generateEvent({
            event: flyoutHoverEvent,
        });
        setIsHovered(false);
    };
    var buttonBehavior = {
        onMouseOver: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onContextMenu: function (evt) {
            evt.preventDefault();
        },
    };
    /**
     * If icons are specified via control data, use those
     * otherwise use sized AppsIcon from internal icon set
     */
    var buttonIcon = React.useMemo(function () {
        return iconSelectedState
            ? selectedIcon !== null && selectedIcon !== void 0 ? selectedIcon : (React.createElement(AppsIconSelected, { size: wide ? 'wide' : 'default', primaryFill: tokens.colorNeutralForeground3BrandSelected }))
            : icon !== null && icon !== void 0 ? icon : (React.createElement(AppsIcon, { size: wide ? 'wide' : 'default', primaryFill: flyoutOpen || isHovered
                    ? tokens.colorNeutralForeground3BrandSelected
                    : tokens.colorNeutralForeground3 }));
    }, [flyoutOpen, icon, iconSelectedState, isHovered, selectedIcon, wide]);
    var onClickHandler = function () {
        changeFlyoutState(!flyoutOpen);
    };
    var setButtonRef = function (buttonRef) {
        // Set the ref to be used in the flyout as trigger target
        targetRef.current = buttonRef;
        // Set the ref for the forwarded ref from the parent
        if (typeof forwardRef === 'function') {
            forwardRef(buttonRef);
        }
        else if (forwardRef) {
            forwardRef.current = buttonRef;
        }
    };
    var LazyFlyoutButtonBadgeRenderer = React.useRef(React.lazy(function () {
        return customImportHandler(import(
        /* webpackChunkName: "appbar-control-flyoutbuttonbadge" */ './FlyoutButtonBadge'));
    }));
    var badgeControl = useMemo(function () {
        if (enabledFlyoutButtonBadgeType &&
            enabledFlyoutButtonBadgeType !== 'None' &&
            flyoutButtonBadgeDescription) {
            var LazyFlyoutButtonBadge = LazyFlyoutButtonBadgeRenderer.current;
            return (React.createElement(AppBarErrorBoundary, null,
                React.createElement(React.Suspense, { fallback: "" },
                    React.createElement(LazyFlyoutButtonBadge, { enabledFlyoutButtonBadgeType: enabledFlyoutButtonBadgeType, flyoutButtonBadgeDescription: flyoutButtonBadgeDescription }))));
        }
        return null;
    }, [enabledFlyoutButtonBadgeType, flyoutButtonBadgeDescription]);
    var triggerAttributes = useModalAttributes().triggerAttributes;
    var flyoutButton = (React.createElement(Tooltip, { content: title, relationship: "label", positioning: "after" },
        React.createElement("div", { className: styles.moreAppsButtonContainer },
            React.createElement(Button, __assign({ ref: setButtonRef, "data-tid": "appbar-flyout-button", onClick: onClickHandler, "aria-haspopup": "dialog", iconPosition: "after", "aria-label": title, tabIndex: 0, icon: React.createElement(TileIcon, { id: "appbar-flyout-button-icon", icon: buttonIcon, viewMode: wide ? ViewMode.Wide : ViewMode.Default, label: text, isSelected: !!iconSelectedState, brandingColor: "", showDotIndicator: false }), className: mergeClasses(styles.moreAppsButton, wide && styles.wideMoreAppsButton, !!iconSelectedState && styles.moreAppsButtonSelected) }, buttonBehavior, triggerAttributes)),
            badgeControl)));
    var handleOpenChange = React.useCallback(function (e, data) {
        var _a;
        if (!((_a = targetRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            // Only handle the open change event from the popover if
            // the target of the event did not happen in the flyout button,
            // which is denoted by targetRef, or one of its descendents in the
            // DOM (e.g. the tile icon).  If the event happened from the flyout
            // button, its click handler will handle the event.
            // See Custom Trigger documentation on Popover for details.
            changeFlyoutState(data.open);
        }
    }, [changeFlyoutState]);
    // Lazy renderer for the flyout - using a ref
    // here so it's sticky across renders
    var LazyFlyoutRenderer = React.useRef(React.lazy(function () {
        return customImportHandler(import(
        /* webpackChunkName: "appbar-control-lazyflyout" */ './LazyFlyout'));
    }));
    // Memoizing the flyout so it doesn't flash as you hover over the flyout button,
    // which causes it to re-render
    var lazyFlyout = React.useMemo(function () {
        var LazyFlyout = LazyFlyoutRenderer.current;
        return flyoutOpen != null && targetRef.current != null ? (React.createElement(AppBarErrorBoundary, null,
            React.createElement(React.Suspense, { fallback: "" },
                React.createElement(LazyFlyout, { targetRef: targetRef.current, ariaLabel: title, emptyStateMessage: emptyStateMessage, suggestedAppsAddButtonText: suggestedAppsAddButtonText, suggestedAppsPrivacyLinkText: suggestedAppsPrivacyLinkText, suggestedAppsPermissionsLinkText: suggestedAppsPermissionsLinkText, suggestedAppsTermsOfUseLinkText: suggestedAppsTermsOfUseLinkText, suggestedAppsSectionTitle: suggestedAppsSectionTitle, flyoutOpen: !!flyoutOpen, handleOpenChange: handleOpenChange, launchTileBehavior: launchTileBehavior, flyoutSize: flyoutSize, closeFlyout: closeFlyout, footerLinks: footerLinks, footerLinkFormat: footerLinkFormat, searchEnabled: searchEnabled, suggestionsEnabled: suggestionsEnabled, searchBoxPlaceholderText: searchBoxPlaceholderText, searchDebounceRate: searchDebounceRate, searchNoResultsTitle: searchNoResultsTitle, searchNoResultsMessage: searchNoResultsMessage, searchResultsSectionTitle: searchResultsSectionTitle, installedAppsSectionAriaLabel: installedAppsSectionAriaLabel, suggestedAppsSectionAriaLabel: suggestedAppsSectionAriaLabel, installedAppsAnnouncementMessage: installedAppsAnnouncementMessage, suggestedAppsAnnouncementMessage: suggestedAppsAnnouncementMessage, installedAndSuggestedAppsAnnouncementMessage: installedAndSuggestedAppsAnnouncementMessage, onPinStateChange: onPinStateChange, customImportHandler: customImportHandler })))) : null;
    }, [
        flyoutOpen,
        title,
        emptyStateMessage,
        suggestedAppsAddButtonText,
        suggestedAppsPrivacyLinkText,
        suggestedAppsPermissionsLinkText,
        suggestedAppsTermsOfUseLinkText,
        suggestedAppsSectionTitle,
        handleOpenChange,
        launchTileBehavior,
        flyoutSize,
        closeFlyout,
        footerLinks,
        footerLinkFormat,
        searchEnabled,
        suggestionsEnabled,
        searchBoxPlaceholderText,
        searchDebounceRate,
        searchNoResultsTitle,
        searchNoResultsMessage,
        searchResultsSectionTitle,
        installedAppsSectionAriaLabel,
        suggestedAppsSectionAriaLabel,
        installedAppsAnnouncementMessage,
        suggestedAppsAnnouncementMessage,
        installedAndSuggestedAppsAnnouncementMessage,
        onPinStateChange,
        customImportHandler,
    ]);
    return (React.createElement(React.Fragment, null,
        flyoutButton,
        lazyFlyout));
};
FlyoutButtonRender.displayName = 'FlyoutButton';
export var FlyoutButton = React.forwardRef(FlyoutButtonRender);
//# sourceMappingURL=FlyoutButton.js.map